import { useState, useTransition } from "react";
import { Sample } from "@/comn/components/_";
import { Layout, Button, Pdf, Grid, FormControl, CommonExcelUpload } from "@/comn/components";
import { useModal, useFetch, TGridSchema, useGrid, useResource } from "@/comn/hooks";
import "prismjs/themes/prism.css";
import { comnEnvs, comnUtils } from "@/comn/utils";
import { useTranslation } from "react-i18next";

export const GuideExcelUploadBackend = () => {
    const modal = useModal();
    const { t } = useTranslation();

    return (
        <Sample title="엑셀업로드, 다운로드(Back-End)" description="">
            <Sample.Section
                title="1. 엑셀업로드 기본 기능"
                description={
                    <div className="leading-5">
                        - 관련 Back-end 소스 프로젝트 : tancis-ext-sample
                        <br />- 관련 Back-end 소스 : /tancis/sample/controller/SampleExcelController.java
                        <br />- comnUtils.importExcel 메소드로 엑셀파일을 Back-end로 전송
                    </div>
                }
            >
                <Sample.Table
                    widths={["w-[15%]", "w-[50%]", "w-auto"]}
                    data={[
                        ["Method", "파라메터", "반환값", "설명"],
                        [
                            "comnUtils.importExcel",
                            "{ url : 엑셀 업로드 주소 , data : 엑셀 파일과 같이 Back-end로 전송할 데이터 Dto}",
                            `엑셀 업로드 결과(back-end return)`,
                            "",
                        ],
                    ]}
                />
                <Sample.Code>{`<Button
    role="tmplDown"
    onClick={() => {
        /*
        포털의 템플릿 관리 메뉴에서 엑셀 템플릿 업로드 및 등록
        http://tancis.singlewindow.info:30090/ptli/ptl/btbdms/ptli1018001q?menuId=menu_osci0156

        해당 게시물의 게시글 ID (UUID) 를 복사하여 
        comnUtils.downloadTemplate 함수에 파라메터로 전달
            */
        comnUtils.downloadTemplate("1b3a4d15-415d-2183-e063-020011ac24c9");
    }}
></Button>
<Button
    role="excelUpload"
    // 비동기 호출 방식 : async 적용
    onClick={async () => {
        // 비동기 호출 방식 : await 를 적용하여 업로드 후에 다음코드를 실행 처리
        const result: any = await comnUtils.importExcel({
            // 엑셀업로드 Back-end 주소
            url: "http://localhost:19430/sample/api/v1/sample/sample-excel/excel-upload",
        });
        /*
        {
            data : back-end 에서 isResClient 값을 true 로 설정한 경우 업로드한 데이터를 리턴
            status : 200 (정상처리한 경우)
            ...
        }                                
        */
        if (result.status === 200) {
            // 엑셀 업로드 성공
            modal.openModal({
                content: t("msg.osc.00006"),
            });
        } else {
            modal.openModal({
                content: t("msg.osc.00045"),
            });
        }
    }}
></Button>
`}</Sample.Code>
                <Sample.Code>{`@PostMapping(value = "/excel-upload")
public void saveSampleExcelUpload(@RequestParam("file") MultipartFile excelFile) throws Exception {

    /*    
    엑셀 데이터를 화면으로 반환할 것인지 여부 default true: 반환, false: 반환하지 않음
    */
    boolean isResClient = true;		
    
    // 엑셀 업로드 실행 설정
    ExcelUploadExecutor executor = new ExcelUploadExecutor();
    executor.setResClient(isResClient);
    // 엑셀 데이터 파싱 후 DB 에 Insert 하는 Handler 설정
    executor.setUploadDataHandler(new SampleUploadDataHandler(service));	    

    List<String> columnList = new ArrayList<>();
    // Dto 에 선언된 field의 순서대로 엑셀의 데이터를 읽어서 세팅하기 위한 column list 세팅
    for (Field field : SampleExcelDto.class.getDeclaredFields()) {
        columnList.add(field.getName());
    }

    executor.execute(excelFile.getInputStream(),
            ImportFileInfo.builder()
                    .dataName("sample")
                    .headRowIdx(1)
                    .skipRowCnt(1)
                    .columnList(columnList)
                    .build(),
            excelFile.getOriginalFilename());
}
`}</Sample.Code>
                <Layout>
                    <Layout.Left>
                        <Button
                            role="tmplDown"
                            onClick={() => {
                                /*
                                포털의 템플릿 관리 메뉴에서 엑셀 템플릿 업로드 및 등록
                                http://tancis.singlewindow.info:30090/ptli/ptl/btbdms/ptli1018001q?menuId=menu_osci0156

                                해당 게시물의 게시글 ID (UUID) 를 복사하여 
                                comnUtils.downloadTemplate 함수에 파라메터로 전달
                                 */
                                comnUtils.downloadTemplate("1b3a4d15-415d-2183-e063-020011ac24c9");
                            }}
                        ></Button>
                        <Button
                            role="excelUpload"
                            // 비동기 호출 방식 : async 적용
                            onClick={async () => {
                                // 비동기 호출 방식 : await 를 적용하여 업로드 후에 다음코드를 실행 처리
                                const result: any = await comnUtils.importExcel({
                                    // 엑셀업로드 Back-end 주소
                                    url: "http://localhost:19430/sample/api/v1/sample/sample-excel/excel-upload",
                                    data: {},
                                });
                                /*
                                {
                                    data : back-end 에서 isResClient 값을 true 로 설정한 경우 업로드한 데이터를 리턴
                                    status : 200 (정상처리한 경우)
                                    ...
                                }                                
                                */
                                if (result.status === 200) {
                                    // 엑셀 업로드 성공
                                    modal.openModal({
                                        content: t("msg.osc.00006"),
                                    });
                                } else {
                                    modal.openModal({
                                        content: t("msg.osc.00045"),
                                    });
                                }
                            }}
                        ></Button>
                    </Layout.Left>
                </Layout>
                <Layout></Layout>
            </Sample.Section>
            <Sample.Section
                title="2. 엑셀업로드 기본 기능(with Dto)"
                description={
                    <div className="leading-5">
                        - 관련 Back-end 소스 프로젝트 : tancis-ext-sample
                        <br />- 관련 Back-end 소스 : /tancis/sample/controller/SampleExcelController.java
                        <br />- comnUtils.importExcel 메소드로 엑셀파일을 Back-end로 전송
                        <br />- comnUtils.importExcel 메소드의 data 옵션으로 Back-end로 함계 전송할 데이터 설정
                    </div>
                }
            >
                <Sample.Code>{`<Button
    role="tmplDown"
    onClick={() => {
        /*
        포털의 템플릿 관리 메뉴에서 엑셀 템플릿 업로드 및 등록
        http://tancis.singlewindow.info:30090/ptli/ptl/btbdms/ptli1018001q?menuId=menu_osci0156

        해당 게시물의 게시글 ID (UUID) 를 복사하여 
        comnUtils.downloadTemplate 함수에 파라메터로 전달
            */
        comnUtils.downloadTemplate("1b3a4d15-415d-2183-e063-020011ac24c9");
    }}
></Button>
<Button
    role="excelUpload"
    // 비동기 호출 방식 : async 적용
    onClick={async () => {
        // 비동기 호출 방식 : await 를 적용하여 업로드 후에 다음코드를 실행 처리
        const result: any = await comnUtils.importExcel({
            // 엑셀업로드 Back-end 주소
            url: "http://localhost:19430/sample/api/v1/sample/sample-excel/excel-upload2",
            data: { mrn: "12345" }, // 엑셀 업로드시 Data도 같이 전송
        });
        /*
        {
            data : back-end 에서 isResClient 값을 true 로 설정한 경우 업로드한 데이터를 리턴
            status : 200 (정상처리한 경우)
            ...
        }                                
        */
        if (result.status === 200) {
            // 엑셀 업로드 성공
            modal.openModal({
                content: t("msg.osc.00006"),
            });
        } else {
            modal.openModal({
                content: t("msg.osc.00045"),
            });
        }
    }}
></Button>
`}</Sample.Code>
                <Sample.Code>{`@PostMapping(value = "/excel-upload2")
// data를 받기 위한 @RequestPart("dto") 추가
public void saveSampleExcelUploadWithAdditionalData(@RequestPart("file") MultipartFile excelFile, @RequestPart("dto") SampleExcelCondDto sampleExcelCondDto) throws Exception {

   /*    
    엑셀 데이터를 화면으로 반환할 것인지 여부 default true: 반환, false: 반환하지 않음
    */
    boolean isResClient = true;		
    
    // dto 에 담긴 값 확인
    log.info("mrn: {}", sampleExcelCondDto.getMrn());

    // 엑셀 업로드 실행 설정
    ExcelUploadExecutor executor = new ExcelUploadExecutor();

    executor.setResClient(isResClient);
    // 엑셀 데이터 파싱 후 DB 에 Insert 하는 Handler 설정
    SampleUploadDataHandler sampleUploadDataHandler = new SampleUploadDataHandler(service);    
    // dto 데이터 엑셀 업로드 handler에 설정(해당 핸들러 소스 확인)
    sampleUploadDataHandler.setSampleExcelCondDto(sampleExcelCondDto);

    executor.setUploadDataHandler(sampleUploadDataHandler);
    List<String> columnList = new ArrayList<>();
    // 엑셀 데이터 파싱 후 DB 에 Insert 하는 Handler 설정
    for (Field field : SampleExcelDto.class.getDeclaredFields()) {
        columnList.add(field.getName());
    }

    executor.execute(excelFile.getInputStream(),
            ImportFileInfo.builder()
                    .dataName("sample")
                    .headRowIdx(1)
                    .skipRowCnt(1)
                    .columnList(columnList)
                    .build(),
            excelFile.getOriginalFilename());
}
`}</Sample.Code>
                <Layout>
                    <Layout.Left>
                        <Button
                            role="tmplDown"
                            onClick={() => {
                                /*
                                포털의 템플릿 관리 메뉴에서 엑셀 템플릿 업로드 및 등록
                                http://tancis.singlewindow.info:30090/ptli/ptl/btbdms/ptli1018001q?menuId=menu_osci0156

                                해당 게시물의 게시글 ID (UUID) 를 복사하여 
                                comnUtils.downloadTemplate 함수에 파라메터로 전달
                                 */
                                comnUtils.downloadTemplate("1b3a4d15-415d-2183-e063-020011ac24c9");
                            }}
                        ></Button>
                        <Button
                            role="excelUpload"
                            // 비동기 호출 방식 : async 적용
                            onClick={async () => {
                                // 비동기 호출 방식 : await 를 적용하여 업로드 후에 다음코드를 실행 처리
                                const result: any = await comnUtils.importExcel({
                                    // 엑셀업로드 Back-end 주소
                                    url: "http://localhost:19430/sample/api/v1/sample/sample-excel/excel-upload2",
                                    data: { mrn: "12345" }, // 엑셀 업로드시 Data도 같이 전송
                                });
                                /*
                                {
                                    data : back-end 에서 isResClient 값을 true 로 설정한 경우 업로드한 데이터를 리턴
                                    status : 200 (정상처리한 경우)
                                    ...
                                }                                
                                */
                                if (result.status === 200) {
                                    // 엑셀 업로드 성공
                                    modal.openModal({
                                        content: t("msg.osc.00006"),
                                    });
                                } else {
                                    modal.openModal({
                                        content: t("msg.osc.00045"),
                                    });
                                }
                            }}
                        ></Button>
                    </Layout.Left>
                </Layout>
                <Layout></Layout>
            </Sample.Section>
            <Sample.Section
                title="3. 엑셀다운로드(back-end)"
                description={
                    <div className="leading-5">
                        - 관련 Back-end 소스 프로젝트 : tancis-ext-sample
                        <br />- 관련 Back-end 소스 : /tancis/sample/controller/SampleExcelController.java
                        <br />- Back-end에 엑셀관련 다운로드 컨트롤러 메소스 생성(post 방식 기본)
                        <br />- comnUtils.exportExcel 메소드로 엑셀파일을 다운로드
                    </div>
                }
            >
                <Sample.Table
                    widths={["w-[15%]", "w-[50%]", "w-auto"]}
                    data={[
                        ["Method", "파라메터", "반환값", "설명"],
                        [
                            "comnUtils.exportExcel",
                            "{ url : 엑셀 다운로드 주소 , data : Back-end로 전송할 데이터 Dto , filename : 다운로드할 엑셀 파일명(확장자 없음, 기본 Export.xlsx)}",
                            `엑셀 파일`,
                            "",
                        ],
                    ]}
                />
                <Sample.Code>{`<Button
    role="excelDown"
    onClick={() => {
        comnUtils.exportExcel({
            // 엑셀 다운로드 주소
            url: "http://localhost:19430/sample/api/v1/sample/sample-excel/excel",
            // back-end에 전송할 데이터
            data: {},
            // 다운로드할 엑셀 파일명(확장자 없이 기재)
            fileName: "Export Excel",
        });
    }}
></Button>
`}</Sample.Code>
                <Sample.Code>{`@PostMapping(value = "/excel")
public void getSampleExcel(@RequestBody SampleExcelCondDto condDto) throws Exception {

    MybatisExcelResultHandler<SampleExcelDto> resultHandler = new MybatisExcelResultHandler<>(
            labelUtil,
            condDto.getFileNameLabel(),
            SampleExcelDto.class
    );

    service.getSampleExcelList(condDto, resultHandler);

    resultHandler.sendRemainData();
}
`}</Sample.Code>
                <Layout>
                    <Layout.Left>
                        <Button
                            role="excelDown"
                            onClick={() => {
                                comnUtils.exportExcel({
                                    // 엑셀 다운로드 주소
                                    url: "http://localhost:19430/sample/api/v1/sample/sample-excel/excel",
                                    // back-end에 전송할 데이터
                                    data: {},
                                    // 다운로드할 엑셀 파일명(확장자 없이 기재)
                                    fileName: "Export Excel",
                                });
                            }}
                        ></Button>
                    </Layout.Left>
                </Layout>
            </Sample.Section>

            <Sample.Section
                title="4. 엑셀업로드 공통 팝업(Back-end)"
                description={
                    <div className="leading-5">
                        - 관련 Back-end 소스 프로젝트 : tancis-ext-sample
                        <br />- 관련 Back-end 소스 : /tancis/sample/controller/SampleExcelController.java
                        <br />- 엑셀을 업로드 하는 공통팝업 컴포넌트(CommonExcelUpload) 사용
                    </div>
                }
            >
                <Sample.Table
                    widths={["w-[15%]", "w-[15%]", "w-auto"]}
                    data={[
                        ["파라메터", "타입", "설명"],
                        ["title", "string(라벨ID)", "엑셀업로드 모달창의 제목"],
                        ["templateId", "string", "업로드 엑셀의 템플릿 아이디"],
                        ["url", "string", "엑셀 업로드 Back-End Api 주소"],
                        ["data", "any", `엑셀 업로드 Back-End Api로 전달될 데이터 @RequestPart("dto")로 수신`],
                        ["callback", "any(function)", `엑셀 업로드 정상완료 후 닫기 버튼 클릭시 실행될 callback 함수`],
                        [
                            "msgList",
                            "any(function)",
                            <>
                                엑셀 업로드 후 오류를 표시하기 위해 Back-End 에서 msgList를 작성한 Object의 경로
                                <br />
                                msgList가 존재하는 경우 그리드에 표시
                            </>,
                        ],
                    ]}
                />
                <Sample.Code>{`<Button
    role="excelUpload"
    onClick={() => {
        modal.openModal({
            content: (
                <CommonExcelUpload
                    title={"Manifest Excel Upload"}
                    templateId="1b3a4d15-415d-2183-e063-020011ac24c9"
                    url="http://localhost:19430/sample/api/v1/sample/sample-excel/excel-upload"
                    data={{ mrn: "12345" }}
                    callback={(data: any) => {
                        modal.closeModal();
                    }}
                    msgList={(data: any) => {
                        return data?.impMnfs?.impMnfsExcel?.content?.msgList;
                    }}
                />
            ),
            draggable: true,
            size: "lg",
        });
        return false;
    }}
></Button>`}</Sample.Code>
                <Layout>
                    <Layout.Left>
                        <Button
                            role="excelUpload"
                            onClick={() => {
                                modal.openModal({
                                    content: (
                                        <CommonExcelUpload
                                            title={"Manifest Excel Upload"}
                                            templateId="1b3a4d15-415d-2183-e063-020011ac24c9"
                                            url="http://localhost:19430/sample/api/v1/sample/sample-excel/excel-upload"
                                            data={{ mrn: "12345" }}
                                            callback={(data: any) => {
                                                modal.closeModal();
                                            }}
                                            msgList={(data: any) => {
                                                return data?.impMnfs?.impMnfsExcel?.content?.msgList;
                                            }}
                                        />
                                    ),
                                    draggable: true,
                                    size: "lg",
                                });
                                return false;
                            }}
                        ></Button>
                    </Layout.Left>
                </Layout>
                <Layout></Layout>
            </Sample.Section>
        </Sample>
    );
};
